import { actionId, mergeLogs, reduce, replayLenient, type Action, type RemoteEvent, type Tournament, actionFingerprint } from '@airmail/engine';
import type { EntityTable } from 'dexie';
import { db, deleteSyncProfiles, loadSyncProfile, newId, saveSyncProfile, saveTournament, setzeOhneCloud } from './db';
import { authHeader, currentUser, ensureFresh } from './auth';
import type { Branding } from './plan';

/**
 * Sync-Client (docs/14 §3). Local-first bleibt: jede Aktion wird zuerst lokal
 * ausgeführt und gespeichert; die Outbox wird bei Netz an den Server geschoben,
 * fremde Aktionen werden geholt und per `mergeLogs` eingespielt.
 *
 * Pro Turnier gespeichert (Tabelle `sync`): API-URL, Key, wie viele Server-
 * Ereignisse bekannt sind und welche lokalen Aktionen noch offen sind.
 */
export interface SyncConfig {
  tournamentId: string;
  apiUrl: string;
  key: string;
  /** höchste bekannte Server-seq */
  seq: number;
  /** Server-Ereignisse (Kopie, damit Merge offline möglich ist) */
  remote: RemoteEvent[];
  /** lokale, noch nicht bestätigte Aktionen */
  outbox: Action[];
  lastSyncAt?: number;
  lastError?: string;
  /**
   * Verworfene eigene Aktionen der letzten Merges. Die Aktion selbst bleibt
   * erhalten, damit die Leitung sie ansehen und notfalls noch einmal anwenden
   * kann (docs/notes/konflikte.md). Ältere Stände kennen `action` nicht — dann
   * lässt sich der Eintrag nur wegklicken.
   */
  rejected: { at: number; type: string; error: string; action?: Action }[];
  /**
   * Schon einmal gemeldete Kollisionen. Eine eigene Aktion, die der Server
   * angenommen hat, bleibt für immer im Protokoll und scheitert bei **jedem**
   * Abgleich aufs Neue — ohne dieses Gedächtnis stünde sie nach dem Wegklicken
   * gleich wieder da. Jede Kollision wird genau einmal gemeldet; was die Leitung
   * damit entschieden hat, bleibt entschieden.
   */
  seenRejected?: string[];
  /** Anmeldungen (docs/15), die schon als Team übernommen wurden */
  importedRegs?: string[];
  /** Zuschauer-Link (docs/17 §9), einmal vom Server geholt */
  viewKey?: string;
  /** Turnier ist einem Konto zugeordnet (docs/16) — sonst versucht der Sync den Claim */
  claimed?: boolean;
  /**
   * Nur **ein** Gerät weist Felder automatisch zu (Spiele-Seite). Vorher tat
   * das jedes Gerät nach jedem Abgleich — Leitung und jeder Helfer starteten
   * dieselben Spiele, und die Kollisionen mussten hinterher als „überholt"
   * aufgeräumt werden. `false` bekommt jedes Gerät, das per Link beigetreten
   * ist; das Gerät, das den Sync eingeschaltet hat, bleibt der Zuweiser.
   * Fehlt das Feld (ältere Profile), gilt wie bisher: zuweisen.
   */
  autoLeader?: boolean;
  /**
   * Rolle **auf diesem Gerät** (docs/notes/helfer.md). „helfer" blendet alles
   * aus, was den Turnierablauf ändern kann — Auslosung, Modus, Teams, Felder —
   * und lässt nur die Spiele übrig, bei `courtGroup` sogar nur die eines Orts.
   *
   * Seit 09/2026 ist das nicht mehr nur Anzeige: Ein Helfer-Gerät hat **keinen
   * Turnier-Key**, sondern einen eigenen `helperKey`, und der Server lässt
   * damit nur Ergebnisse und Spielstart zu. An der Adresse zu drehen bringt
   * nichts mehr — dort steht nichts, woran man drehen könnte.
   */
  role?: 'helfer';
  /**
   * Eigener Schlüssel dieses Helfer-Geräts. Ist er gesetzt, ist `key` leer:
   * Ein Gerät ist entweder Leitung oder Helfer, nie beides.
   */
  helperKey?: string;
  /**
   * Spielort, auf den ein Helfer festgelegt ist (leer = alle). Das bleibt ein
   * Schutz vor Versehen: Der Server kennt nur das Aktionsprotokoll, nicht den
   * Spielstand, und weiß deshalb nicht, auf welchem Feld ein Spiel läuft.
   */
  courtGroup?: string;
}

const table = () => db.table('sync') as EntityTable<SyncConfig, 'tournamentId'>;

/**
 * Schema-Version des Sync-Protokolls (services/sync/src/protocol.ts,
 * `SCHEMA_VERSION`). Geht bei Anlegen und Push mit; ein Server, der sie nicht
 * kennt, ignoriert das Feld, ein älterer Server als die App lehnt mit 400 ab.
 * Fehlt sie in einer Antwort (Ereignisse von vorher), gilt 1.
 */
export const SCHEMA_VERSION = 1;

/** Sync-Adresse aus dem Build; im Dev-Login-Modus (Smoke-Test) der lokale Dev-Server */
export const DEFAULT_API = (import.meta.env.VITE_SYNC_API as string | undefined) ?? (import.meta.env.VITE_AUTH === 'dev' ? 'http://localhost:8787' : '');

/**
 * Ist das unsere eigene Server-Adresse? Nur an sie geht das Login-Token.
 *
 * Die Links (`#/join?…`, `#/live?…`, `#/anmelden?…`) konnten mit `&api=…` eine
 * fremde Adresse mitbringen — und jede Anfrage trug das 12 Stunden gültige
 * Cognito-Token. Ein präparierter Beitreten-Link hätte damit das Konto eines
 * angemeldeten Organisators an einen fremden Server geschickt. Ohne festen
 * `DEFAULT_API` (Entwicklung) gilt jede Adresse als eigene.
 */
export function eigeneApi(apiUrl: string): boolean {
  return !DEFAULT_API || apiUrl.replace(/\/$/, '') === DEFAULT_API.replace(/\/$/, '');
}

/**
 * Darf das Login-Token an diese Adresse? Nur an den eingebauten Server. Ohne
 * festen `DEFAULT_API` gilt das nur in der Entwicklung: Ein Produktions-Build
 * ohne Server-Adresse schickte das Token sonst an jede Adresse aus einem
 * `api=`-Link (`eigeneApi` lässt dort jede Adresse zu).
 */
export function tokenErlaubt(apiUrl: string, prod: boolean = import.meta.env.PROD): boolean {
  if (!DEFAULT_API) return !prod;
  return eigeneApi(apiUrl);
}

/** Server-Adresse aus einem Link: `api=` zählt nur, solange kein fester Server eingebaut ist */
export function apiAusLink(params: URLSearchParams): string {
  const gewuenscht = params.get('api');
  if (gewuenscht && eigeneApi(gewuenscht)) return gewuenscht;
  return DEFAULT_API;
}

/** Geräte-Kennung — stabil je Browser */
export function deviceId(): string {
  try {
    let id = localStorage.getItem('airmail.device');
    if (!id) {
      id = newId().slice(0, 8);
      localStorage.setItem('airmail.device', id);
    }
    return id;
  } catch {
    return 'dev';
  }
}

let lastN = 0;
/**
 * Laufnummer je Gerät: **streng monoton**, auch bei vielen Aktionen in derselben
 * Millisekunde und über Reloads hinweg (der Server verwirft n ≤ letzte bekannte).
 *
 * Der Stand wird bei jedem Aufruf aus `localStorage` mitgelesen, nicht nur beim
 * ersten: Zwei Fenster desselben Browsers (Leitung und Beamer, docs/notes/
 * beamer.md) teilen sich die Geräte-Kennung — und vergäben sonst in derselben
 * Millisekunde dieselbe Nummer, worauf der Server die zweite Aktion als
 * Duplikat verwürfe.
 */
export function nextActionId(): string {
  try {
    lastN = Math.max(lastN, Number(localStorage.getItem('airmail.actionSeq') ?? 0) || 0);
  } catch {
    /* egal */
  }
  const n = Math.max(lastN + 1, Date.now() * 1000);
  lastN = n;
  try {
    localStorage.setItem('airmail.actionSeq', String(n));
  } catch {
    /* egal */
  }
  return actionId(deviceId(), n);
}

/*
 * Die Server-Ereignisse (`remote`) liegen seit Schema-Version 4 in einer
 * eigenen Tabelle und werden nur angehängt (store/db.ts) — vorher schrieb
 * jedes Speichern des Profils die ganze Protokoll-Kopie mit.
 */
export async function loadSync(tournamentId: string): Promise<SyncConfig | undefined> {
  return loadSyncProfile<SyncConfig>(tournamentId);
}
export async function saveSync(cfg: SyncConfig): Promise<void> {
  await saveSyncProfile(cfg);
}
export async function removeSync(tournamentId: string): Promise<void> {
  await deleteSyncProfiles([tournamentId]);
}

export function makeKey(): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyz23456789';
  // 31 Zeichen: 256 mod 31 = 8 → mit `b % 31` wären die ersten acht Zeichen etwas häufiger.
  // Bytes ≥ 248 werden deshalb verworfen und nachgezogen (Rejection Sampling).
  const grenze = 256 - (256 % alphabet.length);
  let out = '';
  while (out.length < 12) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    for (const b of bytes) {
      if (b < grenze && out.length < 12) out += alphabet[b % alphabet.length];
    }
  }
  return out;
}

/** Teilen-Link: öffnet die App und tritt dem Turnier bei */
/** Zuschauer-Link (docs/17 §9): nur lesen — Spielplan, Zeiten, Tabellen */
export function viewLink(cfg: Pick<SyncConfig, 'tournamentId' | 'apiUrl'>, viewKey: string): string {
  const base = `${location.origin}${location.pathname}`;
  const params = new URLSearchParams({ t: cfg.tournamentId, v: viewKey });
  if (cfg.apiUrl !== DEFAULT_API) params.set('api', cfg.apiUrl);
  return `${base}#/live?${params.toString()}`;
}
export async function ensureViewKey(cfg: SyncConfig): Promise<string> {
  const r = await call<{ viewKey: string }>(cfg, 'POST', `/t/${encodeURIComponent(cfg.tournamentId)}/view`);
  return r.viewKey;
}
/** Antwort der Live-Daten: `init`, `name` und `branding` schickt der Server nur beim Abruf ab 0 sicher mit */
export interface LiveAntwort {
  /** höchste seq des Servers */
  seq: number;
  /** bis hierher ist alles geholt — Ausgangspunkt für den nächsten Abruf */
  bis: number;
  init?: Tournament['init'];
  events: RemoteEvent[];
  name?: string;
  branding?: Branding;
  /** Der Server kennt weniger als `since` — alles neu aufbauen */
  reset?: boolean;
}

/** Öffentliche Live-Daten (ohne Key) — `branding` nur, wenn der Eigentümer Pro hat (docs/21 §2) */
export async function fetchLive(apiUrl: string, tournamentId: string, viewKey: string, since = 0): Promise<LiveAntwort> {
  /*
   * In Seiten: Der Server gibt höchstens 1000 Ereignisse je Antwort. Ohne
   * Nachladen zeigte der Beamer bei einem großen Turnier (ab etwa 40 Teams)
   * einen alten, falschen Stand — ohne jeden Hinweis.
   */
  type Seite = ProtokollSeite & { init?: Tournament['init']; name?: string; branding?: Branding };
  const pull = await holeProtokoll((s) => apiCall<Seite>(apiUrl, undefined, 'GET', `/t/${encodeURIComponent(tournamentId)}/live/${encodeURIComponent(viewKey)}?since=${s}`), since);
  const k = pull.kopf;
  const out: LiveAntwort = { seq: k.seq, bis: pull.bis, events: pull.events };
  if (k.init) out.init = k.init;
  if (k.name) out.name = k.name;
  if (k.branding) out.branding = k.branding;
  // Weiß der Server weniger als wir (Turnier zurückgesetzt), stimmt der Stand hier nicht mehr
  if (pull.reset || k.seq < since) out.reset = true;
  return out;
}

/** Was die Zuschauer-Ansicht zwischen zwei Abrufen behält (docs/17 §9) */
export interface LiveStand {
  init: Tournament['init'];
  events: RemoteEvent[];
  bis: number;
  state: Tournament;
  branding?: Branding;
}

/**
 * Neue Live-Ereignisse auf den bisherigen Stand setzen. Vorher holte die
 * Zuschauer-Ansicht alle 10–20 s das **ganze** Protokoll ab 0 und spielte es
 * neu ab — auf jedem Handy in der Halle. Jetzt kommt nur, was neu ist, und
 * wird mit dem strengen `reduce` obendrauf gesetzt. Was dort scheitert, fiele
 * auch beim toleranten Replay heraus; es wird genauso übersprungen. Neu
 * aufgebaut wird nur, wenn ein Widerruf kommt (er ändert Vergangenes), wenn
 * der Server zurückgesetzt wurde oder wenn noch kein Stand da ist.
 *
 * `null` heißt: bitte alles ab 0 holen und `liveNeu` nehmen.
 */
export function liveFortschreiben(stand: LiveStand, antwort: LiveAntwort): LiveStand | null {
  if (antwort.reset) return null;
  const neu = antwort.events.filter((e) => e.seq > stand.bis);
  const branding = antwort.branding ?? stand.branding;
  if (neu.length === 0) return antwort.bis > stand.bis || branding !== stand.branding ? { ...stand, bis: Math.max(stand.bis, antwort.bis), ...(branding ? { branding } : {}) } : stand;
  let state = stand.state;
  for (const e of neu) {
    const a = e.action;
    if (a.type === 'undo') continue;
    if (a.type === 'revert') return null;
    const r = reduce(state, a);
    if (r.ok) state = r.state;
  }
  const out: LiveStand = { init: stand.init, events: [...stand.events, ...neu], bis: Math.max(stand.bis, antwort.bis), state };
  if (branding) out.branding = branding;
  return out;
}

/** Stand aus einem vollständigen Abruf ab 0 */
export function liveNeu(antwort: LiveAntwort): LiveStand | null {
  if (!antwort.init) return null;
  const { state } = replayLenient(
    antwort.init,
    antwort.events.map((e) => e.action),
  );
  const out: LiveStand = { init: antwort.init, events: antwort.events, bis: antwort.bis, state };
  if (antwort.branding) out.branding = antwort.branding;
  return out;
}

/**
 * Ergebnismeldung durch Spieler (docs/notes/reports.md, docs/notes/live-melden.md).
 * Öffentlich wie `fetchLive` — nur der Zuschauer-Schlüssel, kein Konto, kein
 * Auth-Header. Der Server legt die Meldung ab; die Turnierleitung bestätigt sie.
 */
export interface ResultReport {
  id: string;
  matchId: string;
  a: number;
  b: number;
  by?: string;
  createdAt: number;
}
export async function reportResult(apiUrl: string, tournamentId: string, viewKey: string, body: { matchId: string; a: number; b: number; by?: string }): Promise<{ id: string }> {
  const r = await fetch(`${apiUrl.replace(/\/$/, '')}/t/${encodeURIComponent(tournamentId)}/live/${encodeURIComponent(viewKey)}/report`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = (await r.json().catch(() => ({}))) as { error?: string; id?: string };
  if (!r.ok) throw new Error(data.error ?? `HTTP ${r.status}`);
  return { id: data.id ?? '' };
}
/** Leitung/Helfer: offene Meldungen der Spieler, älteste zuerst */
export async function listReports(cfg: SyncConfig): Promise<ResultReport[]> {
  const r = await call<{ reports: ResultReport[] }>(cfg, 'GET', `/t/${encodeURIComponent(cfg.tournamentId)}/reports`);
  return r.reports ?? [];
}
/** Meldung wegräumen — nach dem Bestätigen wie nach dem Verwerfen */
export async function deleteReport(cfg: SyncConfig, rid: string): Promise<void> {
  await call(cfg, 'DELETE', `/t/${encodeURIComponent(cfg.tournamentId)}/reports/${encodeURIComponent(rid)}`);
}

/** Ein ausgegebener Helfer-Link, wie der Server ihn führt */
export interface HelperKey {
  key: string;
  label: string;
  courtGroup?: string;
  createdAt: number;
}

export function listHelpers(cfg: SyncConfig): Promise<{ helpers: HelperKey[] }> {
  return call<{ helpers: HelperKey[] }>(cfg, 'GET', `/t/${encodeURIComponent(cfg.tournamentId)}/helpers`);
}
export function addHelper(cfg: SyncConfig, body: { label?: string; courtGroup?: string }): Promise<HelperKey> {
  return call<HelperKey>(cfg, 'POST', `/t/${encodeURIComponent(cfg.tournamentId)}/helpers`, body);
}
export function removeHelper(cfg: SyncConfig, helperKey: string): Promise<{ helpers: HelperKey[] }> {
  return call<{ helpers: HelperKey[] }>(cfg, 'DELETE', `/t/${encodeURIComponent(cfg.tournamentId)}/helpers/${encodeURIComponent(helperKey)}`);
}

/**
 * Link für die **Turnierleitung** auf einem zweiten Gerät — trägt den
 * Turnier-Key und kann alles.
 */
export function joinLink(cfg: Pick<SyncConfig, 'tournamentId' | 'key' | 'apiUrl'>): string {
  const base = `${location.origin}${location.pathname}`;
  const params = new URLSearchParams({ t: cfg.tournamentId, k: cfg.key });
  if (cfg.apiUrl !== DEFAULT_API) params.set('api', cfg.apiUrl);
  return `${base}#/join?${params.toString()}`;
}

/**
 * Link für einen **Helfer** — trägt nur dessen eigenen Schlüssel. Weder Rolle
 * noch Ort stehen darin: Beides liegt beim Schlüssel auf dem Server, sonst
 * ließe sich das Eine wie das Andere durch Streichen aus der Adresse aufheben.
 */
export function helperLink(cfg: Pick<SyncConfig, 'tournamentId' | 'apiUrl'>, helperKey: string): string {
  const base = `${location.origin}${location.pathname}`;
  const params = new URLSearchParams({ t: cfg.tournamentId, h: helperKey });
  if (cfg.apiUrl !== DEFAULT_API) params.set('api', cfg.apiUrl);
  return `${base}#/join?${params.toString()}`;
}

/**
 * Anmelde-Link zum **Teilen** (WhatsApp, Facebook): `https://<App>/a/<id>/<regKey>`.
 * Diese Adresse beantwortet der Server (über CloudFront, docs/14 §5) mit einer
 * kleinen Seite, die Turniername, Datum und Ort als Open-Graph-Vorschau trägt
 * und sofort auf `#/anmelden?t=…&r=…` weiterleitet — ein Hash-Router kann
 * einem Messenger sonst keine Vorschau liefern. Ohne eingebaute App-Adresse
 * (`VITE_APP_URL`, setzt scripts/deploy.sh) oder mit fremdem Server bleibt es
 * beim bisherigen Hash-Link, damit Entwicklung und Browserläufe weiter gehen.
 */
export const APP_URL = ((import.meta.env.VITE_APP_URL as string | undefined) ?? '').replace(/\/$/, '');
export function anmeldeLinkTeilen(cfg: Pick<SyncConfig, 'tournamentId' | 'apiUrl'>, regKey: string): string {
  if (APP_URL && cfg.apiUrl === DEFAULT_API) return `${APP_URL}/a/${encodeURIComponent(cfg.tournamentId)}/${encodeURIComponent(regKey)}`;
  const base = `${location.origin}${location.pathname}`;
  const params = new URLSearchParams({ t: cfg.tournamentId, r: regKey });
  if (cfg.apiUrl !== DEFAULT_API) params.set('api', cfg.apiUrl);
  return `${base}#/anmelden?${params.toString()}`;
}

// ---------------------------------------------------------------------------
// HTTP
// ---------------------------------------------------------------------------

/**
 * Aufruf der Sync-API. `key` = Turnier-Key (Helfer-Link); fehlt bei den öffentlichen
 * Anmelde-Endpunkten. Ist ein Organisator angemeldet, geht sein Token immer mit —
 * der Server akzeptiert Eigentümer oder Key (docs/16).
 */
/**
 * Fehler vom Server samt Status und Rumpf. Der Rumpf wird gebraucht: Beim
 * abgelehnten Helfer-Stapel steht darin, welche Aktionen der Server nicht
 * annimmt (`forbidden`) — nur damit kann das Gerät sie loswerden, statt sie
 * ewig erneut zu schicken.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public body: Record<string, unknown> = {},
  ) {
    super(message);
  }
  /** Aktionen, die der Server dauerhaft ablehnt — erneutes Senden hilft nicht */
  get forbidden(): string[] {
    return Array.isArray(this.body.forbidden) ? (this.body.forbidden as unknown[]).filter((x): x is string => typeof x === 'string') : [];
  }
  /** 402 (docs/21 §2): welches Merkmal der Stufe fehlt — die App erklärt es mit `stufenHinweis` */
  get feature(): string | undefined {
    return this.status === 402 && typeof this.body.feature === 'string' ? this.body.feature : undefined;
  }
}

export async function apiCall<T>(apiUrl: string, key: string | undefined, method: string, path: string, body?: unknown, helperKey?: string): Promise<T> {
  await ensureFresh();
  // Das Konto-Token nur an den eigenen Server — nie an eine Adresse aus einem Link (siehe `tokenErlaubt`)
  const headers: Record<string, string> = { 'content-type': 'application/json', ...(tokenErlaubt(apiUrl) ? authHeader() : {}) };
  // Entweder Leitung oder Helfer — nie beides, sonst könnte ein Helfer-Gerät
  // mit einem nebenher mitgeschickten Turnier-Key alles umgehen
  if (helperKey) headers['x-airmail-helper'] = helperKey;
  else if (key) headers['x-airmail-key'] = key;
  const r = await fetch(`${apiUrl.replace(/\/$/, '')}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  const text = await r.text();
  let data: T & { error?: string };
  try {
    data = text ? (JSON.parse(text) as T & { error?: string }) : ({} as T & { error?: string });
  } catch {
    throw new Error(`Server antwortet nicht mit JSON (HTTP ${r.status})`);
  }
  if (!r.ok) throw new ApiError(data.error ?? `HTTP ${r.status}`, r.status, data as Record<string, unknown>);
  return data;
}

function call<T>(cfg: Pick<SyncConfig, 'apiUrl' | 'key'> & { helperKey?: string }, method: string, path: string, body?: unknown): Promise<T> {
  return apiCall<T>(cfg.apiUrl, cfg.key, method, path, body, cfg.helperKey);
}

/**
 * Eine Seite von `GET /t/{id}/events?since=…`. Neuere Server nennen dazu
 * `last` (seq des letzten zurückgegebenen Ereignisses) und `more` (es gibt
 * noch mehr); ältere nicht — dann wird geblättert, bis `seq` erreicht ist.
 */
export interface ProtokollSeite {
  seq: number;
  events: (RemoteEvent | null)[];
  last?: number;
  more?: boolean;
  reset?: true;
}

export interface Geholt<K> {
  /** erste Seite (bei `reset` die erste Seite ab 0) — trägt `init`, `key`, `helper` … */
  kopf: K;
  events: RemoteEvent[];
  /** bis zu dieser seq ist alles geholt */
  bis: number;
  /** der Server kannte weniger als `since` — es wurde von vorn geholt */
  reset: boolean;
}

/**
 * Protokoll ab `since` holen, **alle** Seiten (der Server gibt höchstens 1000
 * Ereignisse je Antwort). Beitreten und „Sync einschalten" holten vorher nur
 * die erste Seite, merkten sich aber die volle `seq` des Servers — einem
 * Helfer fehlten bei einem großen Turnier alle Ereignisse ab 1001, für immer.
 */
export async function holeProtokoll<K extends ProtokollSeite>(hole: (since: number) => Promise<K>, since0: number): Promise<Geholt<K>> {
  let since = since0;
  let kopf: K | undefined;
  let reset = false;
  let events: RemoteEvent[] = [];
  for (let guard = 0; guard < 50; guard++) {
    const r = await hole(since);
    if (r.reset && !reset) {
      /*
       * Der Server kennt weniger als wir: aus einem Backup zurückgeholt oder
       * das Turnier neu angelegt. Dann von vorn holen (siehe `syncOnce`).
       */
      reset = true;
      since = 0;
      events = [];
      kopf = undefined;
      continue;
    }
    kopf ??= r;
    // Defensiv: nur echte Ereignisse mit fortschreitender Laufnummer übernehmen — sonst könnte die Schleife hängen
    const page = (Array.isArray(r.events) ? r.events : []).filter((e): e is RemoteEvent => !!e && typeof e.seq === 'number' && e.seq > since && !!e.action);
    events.push(...page);
    const zuletzt = typeof r.last === 'number' ? r.last : page[page.length - 1]?.seq;
    const weiter = typeof r.more === 'boolean' ? r.more : zuletzt !== undefined && zuletzt < r.seq;
    if (zuletzt === undefined || zuletzt <= since) break;
    since = zuletzt;
    if (!weiter) break;
  }
  return { kopf: kopf as K, events, bis: since, reset };
}

/** Turnier auf dem Server anlegen (oder mit gleichem Key bestätigen) — gehört dem angemeldeten Konto */
export async function enableSync(t: Tournament, apiUrl: string, key = makeKey()): Promise<SyncConfig> {
  const r = await call<{ created?: boolean }>({ apiUrl, key }, 'PUT', `/t/${encodeURIComponent(t.id)}`, { init: t.init, name: t.name, date: t.date, schema: SCHEMA_VERSION });
  let cfg: SyncConfig;
  if (r && r.created === false) {
    // Der Server kennt das Turnier schon (zweites Gerät, Liste neu geladen, altes Sync-Profil weg):
    // Protokoll holen und nur das in die Outbox legen, was dort fehlt — sonst landet jede Aktion
    // ein zweites Mal auf dem Server (128 Teams statt 8)
    const pull = await holeProtokoll((since) => call<ProtokollSeite & { key?: string }>({ apiUrl, key }, 'GET', `/t/${encodeURIComponent(t.id)}/events?since=${since}`), 0);
    const ids = new Set(pull.events.map((e) => e.action.id).filter((id): id is string => !!id));
    const bodies = new Set(pull.events.map((e) => actionFingerprint(e.action)));
    const outbox = t.log.filter((a) => !(a.id && ids.has(a.id)) && !bodies.has(actionFingerprint(a))).map((a) => (a.id ? a : { ...a, id: nextActionId() }));
    cfg = { tournamentId: t.id, apiUrl, key: pull.kopf.key ?? key, seq: pull.bis, remote: pull.events, outbox, rejected: [], claimed: !!currentUser(), lastSyncAt: Date.now(), autoLeader: true };
  } else {
    cfg = { tournamentId: t.id, apiUrl, key, seq: 0, remote: [], outbox: t.log.map((a) => (a.id ? a : { ...a, id: nextActionId() })), rejected: [], claimed: !!currentUser(), autoLeader: true };
  }
  await saveSync(cfg);
  // Wer den Sync ausdrücklich einschaltet (Liste „In die Cloud", Einstellungen → Cloud, Assistent), will es wieder in der Cloud
  await setzeOhneCloud(t.id, false);
  return cfg;
}

/**
 * Turnier-Key wechseln (nur mit Konto). Danach gilt der alte Schlüssel nicht
 * mehr — ein zweites Leitungsgerät muss neu beitreten; Helfer-Links und
 * Zuschauer-Link bleiben. Der neue Key kommt zurück und wird hier gespeichert.
 */
export async function rotateKey(cfg: SyncConfig): Promise<string> {
  const r = await call<{ key: string }>(cfg, 'POST', `/t/${encodeURIComponent(cfg.tournamentId)}/key`);
  return r.key;
}

/** Konto-Auskunft (DSGVO Art. 15): alles Eigene auf dem Server als eine Datei */
export function exportAccount(apiUrl: string): Promise<unknown> {
  return apiCall<unknown>(apiUrl, undefined, 'GET', '/me/export');
}

/**
 * Konto auf dem Server löschen (DSGVO Art. 17): eigene Turniere, Saisons,
 * Mitgliedschaften. Lokale Turniere bleiben auf dem Gerät — nur die
 * Sync-Profile der **eigenen** Turniere werden gelöscht, denn ihr Gegenstück
 * auf dem Server ist weg. Turniere, denen dieses Gerät über den Leitungs- oder
 * Helfer-Link eines anderen beigetreten ist, gibt es weiter; vorher verloren
 * sie hier ebenfalls ihren Sync — mitten im Turnier eines anderen.
 */
export async function deleteAccount(apiUrl: string): Promise<{ tournaments: number; seasons: number; memberships: number }> {
  // Vorher fragen, was dem Konto gehört — danach weiß es der Server nicht mehr
  const eigene = (await listCloudTournaments(apiUrl)).filter((t) => t.role === 'owner').map((t) => t.id);
  const r = await apiCall<{ tournaments: number; seasons: number; memberships: number }>(apiUrl, undefined, 'DELETE', '/me');
  if (eigene.length) await deleteSyncProfiles(eigene);
  return r;
}

/** Einem fremden Turnier beitreten (Helfer-Link): init holen, lokal anlegen, danach normal syncen */
export async function joinTournament(
  apiUrl: string,
  tournamentId: string,
  key: string,
  helperKey?: string,
  opts: { speichern?: boolean } = {},
): Promise<{ cfg: SyncConfig; init: Tournament['init'] }> {
  const pull = await holeProtokoll(
    (since) =>
      call<ProtokollSeite & { init: Tournament['init']; helper?: { label: string; courtGroup?: string } }>(
        { apiUrl, key, helperKey },
        'GET',
        `/t/${encodeURIComponent(tournamentId)}/events?since=${since}`,
      ),
    0,
  );
  const cfg: SyncConfig = { tournamentId, apiUrl, key, seq: pull.bis, remote: pull.events, outbox: [], rejected: [], lastSyncAt: Date.now(), autoLeader: false };
  if (helperKey) {
    cfg.helperKey = helperKey;
    cfg.role = 'helfer';
    cfg.key = '';
    // Der Ort kommt vom Server, nicht aus dem Link — sonst ließe er sich wegstreichen
    if (pull.kopf.helper?.courtGroup) cfg.courtGroup = pull.kopf.helper.courtGroup;
  }
  // Ohne Speichern: der Aufrufer (Beitreten-Seite) entscheidet selbst, was mit einem vorhandenen Stand geschieht
  if (opts.speichern !== false) await saveSync(cfg);
  return { cfg, init: pull.kopf.init };
}

/** Eigenes Turnier (Konto) auf ein weiteres Gerät holen — ohne Link, der Server gibt dem Eigentümer den Key mit */
export async function joinOwnTournament(apiUrl: string, tournamentId: string): Promise<{ cfg: SyncConfig; init: Tournament['init'] }> {
  const pull = await holeProtokoll(
    (since) => apiCall<ProtokollSeite & { init: Tournament['init']; key?: string }>(apiUrl, undefined, 'GET', `/t/${encodeURIComponent(tournamentId)}/events?since=${since}`),
    0,
  );
  const cfg: SyncConfig = { tournamentId, apiUrl, key: pull.kopf.key ?? '', seq: pull.bis, remote: pull.events, outbox: [], rejected: [], lastSyncAt: Date.now(), claimed: true, autoLeader: false };
  await saveSync(cfg);
  return { cfg, init: pull.kopf.init };
}

export interface CloudTournament {
  id: string;
  name: string;
  date?: string;
  createdAt: number;
  seq: number;
  key: string;
  registrationOpen: boolean;
  role: 'owner' | 'member';
  ownerEmail?: string;
  /**
   * Stand vom Server (services/sync protocol.ts `statusNachAktionen`): geplant beim Anlegen, „läuft" ab dem
   * ersten Spielstart, sonst nur per `tournament.setStatus`. Fehlt bei Turnieren von vor dem Feld, bis
   * der nächste Push ihn setzt — die Liste zeigt sie dann nur unter „Alle".
   */
  status?: Tournament['status'];
}

export interface Member {
  email: string;
  invitedAt: number;
  invitedBy?: string;
}
export async function listMembers(cfg: SyncConfig): Promise<{ ownerEmail?: string; members: Member[]; role: 'owner' | 'member' | 'key' }> {
  return call(cfg, 'GET', `/t/${encodeURIComponent(cfg.tournamentId)}/members`);
}
export async function addMember(cfg: SyncConfig, email: string): Promise<Member[]> {
  return (await call<{ members: Member[] }>(cfg, 'PUT', `/t/${encodeURIComponent(cfg.tournamentId)}/members`, { email })).members;
}
export async function removeMember(cfg: SyncConfig, email: string): Promise<Member[]> {
  return (await call<{ members: Member[] }>(cfg, 'DELETE', `/t/${encodeURIComponent(cfg.tournamentId)}/members/${encodeURIComponent(email)}`)).members;
}

/** IDs aller Turniere mit Sync auf diesem Gerät */
export async function listSyncIds(): Promise<Set<string>> {
  return new Set((await table().toCollection().primaryKeys()) as string[]);
}

/**
 * Turnier ins Konto sichern: Aktionen bekommen IDs (in Protokoll-Reihenfolge),
 * lokal speichern, auf dem Server anlegen. Für Assistent und Turnierliste.
 */
export async function enableSyncFor(t: Tournament, apiUrl = DEFAULT_API): Promise<{ tournament: Tournament; cfg: SyncConfig }> {
  // läuft der Sync für dieses Turnier schon auf diesem Gerät, gibt es nichts zu tun (verhindert doppeltes Hochladen)
  const existing = await loadSync(t.id);
  if (existing) return { tournament: t, cfg: existing };
  const withIds: Tournament = { ...t, log: t.log.map((a) => ({ ...a, id: nextActionId() })) };
  await saveTournament(withIds);
  const cfg = await enableSync(withIds, apiUrl);
  return { tournament: withIds, cfg };
}
/** Turniere des angemeldeten Kontos */
export async function listCloudTournaments(apiUrl: string): Promise<CloudTournament[]> {
  const r = await apiCall<{ tournaments: CloudTournament[] }>(apiUrl, undefined, 'GET', '/me/tournaments');
  return r.tournaments;
}
/** Turnier samt Protokoll und Anmeldungen vom Server löschen — nur Eigentümer */
export async function deleteCloudTournament(apiUrl: string, id: string): Promise<void> {
  await apiCall(apiUrl, undefined, 'DELETE', `/t/${encodeURIComponent(id)}`);
}

/** Turnier von vor den Konten dem eigenen Konto zuordnen (braucht Login + Key) */
async function claimIfNeeded(cfg: SyncConfig): Promise<SyncConfig> {
  if (cfg.claimed || !currentUser() || !cfg.key) return cfg;
  try {
    await call(cfg, 'POST', `/t/${encodeURIComponent(cfg.tournamentId)}/claim`);
    return { ...cfg, claimed: true };
  } catch (e) {
    // gehört einem anderen Konto → nicht weiter versuchen; Helfer-Zugriff über den Key bleibt
    if (/anderen Konto/.test((e as Error).message)) return { ...cfg, claimed: true };
    return cfg;
  }
}

export interface SyncOutcome {
  cfg: SyncConfig;
  /** neuer Zustand, falls sich durch fremde Aktionen etwas geändert hat */
  state?: Tournament;
  pushed: number;
  pulled: number;
  rejected: number;
}

/**
 * Ein Sync-Durchlauf: Outbox pushen → alles Neue pullen → mergen.
 * Wirft bei Netzfehlern; der Aufrufer merkt sich lastError und versucht es später.
 */
export async function syncOnce(cfgIn: SyncConfig, current: Tournament): Promise<SyncOutcome> {
  let cfg = await claimIfNeeded(cfgIn);
  let pushed = 0;
  /*
   * Aktionen, die der Server dauerhaft ablehnt (ein Helfer-Gerät, das etwas
   * versucht hat, das ihm nicht zusteht). Sie müssen aus der Outbox heraus,
   * sonst schickt das Gerät sie bei jedem Takt erneut, bekommt wieder 403 —
   * und weil der Abgleich dabei abbricht, kommt auch nichts mehr herein. Genau
   * so stand ein Helfer-Handy fest („Cloud-Sync meldet einen Fehler … Bis das
   * behoben ist: Backup ziehen"). Sie landen in derselben Liste wie
   * Kollisionen, damit die Eingabe nicht stillschweigend verschwindet.
   */
  const abgelehnt: SyncConfig['rejected'] = [];
  let outbox = cfg.outbox;
  if (outbox.length > 0) {
    for (let i = 0; i < outbox.length; i += 200) {
      const chunk = outbox.slice(i, i + 200);
      try {
        const r = await call<{ seq: number; accepted: number; duplicates: number }>(cfg, 'POST', `/t/${encodeURIComponent(cfg.tournamentId)}/events`, {
          actions: chunk,
          schema: SCHEMA_VERSION,
          status: current.status,
        });
        pushed += r.accepted;
      } catch (e) {
        if (!(e instanceof ApiError) || e.forbidden.length === 0) throw e;
        const weg = new Set(e.forbidden);
        const vorher = outbox.length;
        for (const a of chunk) if (a.id && weg.has(a.id)) abgelehnt.push({ at: Date.now(), type: a.type, error: e.message, action: a });
        outbox = outbox.filter((a) => !(a.id && weg.has(a.id)));
        // Nennt der Server nichts, was hier liegt, wäre der nächste Versuch derselbe — dann lieber Fehler als Endlosschleife
        if (outbox.length === vorher) throw e;
        // Denselben Block ohne die beanstandeten Aktionen noch einmal
        i -= 200;
      }
    }
  }
  cfg = { ...cfg, outbox };
  // Pull in Seiten, bis der Server nichts mehr hat
  const pull = await holeProtokoll((since) => call<ProtokollSeite>(cfg, 'GET', `/t/${encodeURIComponent(cfg.tournamentId)}/events?since=${since}`), cfg.seq);
  const fresh = pull.events;
  const zurueckgesetzt = pull.reset;
  if (zurueckgesetzt) {
    /*
     * Der Server kennt weniger als wir: aus einem Backup zurückgeholt oder
     * das Turnier neu angelegt. Vorher pullte das Gerät für immer ins Leere
     * und alle Geräte liefen still auseinander. Jetzt: von vorn holen, und
     * alles, was der Server nicht mehr hat, wandert zurück in die Outbox —
     * er bekommt nach, was ihm fehlt.
     */
    const bekannt = new Set(fresh.map((e) => e.action.id).filter((id): id is string => !!id));
    const nachreichen = [...cfg.remote.map((e) => e.action), ...cfg.outbox].filter((a) => a.id && !bekannt.has(a.id));
    cfg = { ...cfg, remote: [], seq: 0, outbox: nachreichen };
  }
  const remote = [...cfg.remote, ...fresh];
  const seq = Math.max(remote[remote.length - 1]?.seq ?? 0, pull.bis, cfg.seq);
  /*
   * Zusammenführen nur, wenn es etwas zusammenzuführen gibt. `mergeLogs`
   * spielt das ganze Protokoll neu ab — bei 64 Teams über 1500 Aktionen — und
   * das lief vorher bei **jedem** Takt, auch wenn nichts Neues da war.
   *
   * Ebenso wenig, wenn nur das eigene Echo zurückkam: Nach jedem Push holt der
   * nächste Pull genau die eben geschickten Aktionen wieder ab. Stehen sie in
   * derselben Reihenfolge vorn in der Outbox, ist die Abfolge „Server + Outbox"
   * dieselbe wie vorher — der Stand also auch. Dann wandern sie nur aus der
   * Outbox ins Server-Protokoll, ohne das ganze Turnier neu abzuspielen.
   */
  const nichtsNeues = fresh.length === 0 && abgelehnt.length === 0 && !zurueckgesetzt;
  const nurEcho = !nichtsNeues && abgelehnt.length === 0 && !zurueckgesetzt && istEigenesEcho(fresh, cfg.outbox) && !fehltEtwas(current, fresh);
  const merged = nichtsNeues
    ? { state: current, rejected: [], outbox: cfg.outbox }
    : nurEcho
      ? { state: current, rejected: [], outbox: cfg.outbox.slice(fresh.length) }
      : mergeLogs({ init: current.init, remote, outbox: cfg.outbox });
  // Nur wirklich neue Kollisionen melden (siehe `seenRejected`)
  const seen = new Set(cfg.seenRejected ?? []);
  const frischVerworfen = merged.rejected.filter((r) => !seen.has(rejectKey(r.action)));
  const next: SyncConfig = {
    ...cfg,
    seq,
    remote,
    outbox: merged.outbox,
    lastSyncAt: Date.now(),
    rejected: dedupe([...abgelehnt, ...frischVerworfen.map((r) => ({ at: Date.now(), type: r.action.type, error: r.error, action: r.action })), ...cfg.rejected]).slice(0, 20),
    seenRejected: [...merged.rejected.map((r) => rejectKey(r.action)), ...(cfg.seenRejected ?? [])].filter((k, i, all) => all.indexOf(k) === i).slice(0, 200),
  };
  delete next.lastError;
  // Nur schreiben, wenn sich etwas geändert hat — nicht alle 8 Sekunden dasselbe Profil samt Protokoll-Kopie
  if (wesentlichAnders(cfgIn, next)) await saveSync(next);
  /*
   * Zustand nur ersetzen, wenn etwas dazukam, das dieser Stand noch nicht
   * kennt, oder etwas verworfen wurde. Maßstab ist das eigene Protokoll, nicht
   * die Geräte-Kennung: Vorher galt alles vom eigenen Gerät als „schon
   * bekannt" — und ein zweites Fenster desselben Browsers (Beamer neben der
   * Leitung) bekam die Ergebnisse des ersten nie zu sehen, obwohl es sie
   * vom Server holte.
   */
  const changed = fehltEtwas(current, fresh) || frischVerworfen.length > 0 || abgelehnt.length > 0 || zurueckgesetzt;
  const out: SyncOutcome = { cfg: next, pushed, pulled: fresh.length, rejected: frischVerworfen.length + abgelehnt.length };
  if (changed) out.state = merged.state;
  return out;
}

/** Sind die geholten Ereignisse genau der Anfang der eigenen Outbox, in derselben Reihenfolge? */
export function istEigenesEcho(fresh: readonly RemoteEvent[], outbox: readonly Action[]): boolean {
  if (fresh.length === 0 || fresh.length > outbox.length) return false;
  return fresh.every((e, i) => !!e.action.id && e.action.id === outbox[i]?.id);
}

/**
 * Hat sich an einem Profil mehr als die Uhrzeit des letzten Abgleichs
 * geändert? `lastSyncAt` allein ist keinen Schreibvorgang wert — es wird mit
 * der nächsten echten Änderung mitgespeichert.
 */
export function wesentlichAnders(a: SyncConfig, b: SyncConfig): boolean {
  if (a === b) return false;
  const ids = (xs: readonly Action[]) => xs.map((x) => x.id ?? actionFingerprint(x)).join('|');
  const { lastSyncAt: _a, outbox: oa, remote: ra, ...restA } = a;
  const { lastSyncAt: _b, outbox: ob, remote: rb, ...restB } = b;
  if (a.seq !== b.seq || ra.length !== rb.length || oa.length !== ob.length) return true;
  if (ids(oa) !== ids(ob)) return true;
  if ((ra[ra.length - 1]?.seq ?? 0) !== (rb[rb.length - 1]?.seq ?? 0)) return true;
  // Feld für Feld, fehlende Listen zählen als leer — die Reihenfolge der Felder ist egal
  const ra2 = restA as Record<string, unknown>;
  const rb2 = restB as Record<string, unknown>;
  const leer = (k: string, v: unknown) => (v === undefined && (k === 'rejected' || k === 'seenRejected' || k === 'importedRegs') ? [] : v);
  for (const k of new Set([...Object.keys(ra2), ...Object.keys(rb2)])) {
    if (JSON.stringify(leer(k, ra2[k])) !== JSON.stringify(leer(k, rb2[k]))) return true;
  }
  return false;
}

/** Steht unter den geholten Ereignissen eines, das im Protokoll dieses Stands fehlt? */
export function fehltEtwas(current: Pick<Tournament, 'log'>, fresh: readonly RemoteEvent[]): boolean {
  if (fresh.length === 0) return false;
  const bekannt = new Set(current.log.map((a) => a.id).filter((id): id is string => !!id));
  return fresh.some((e) => !e.action.id || !bekannt.has(e.action.id));
}

/**
 * Aktionen, die **während** eines Abgleichs eingetragen wurden, in das
 * Ergebnis des Abgleichs nachziehen (siehe `syncNow` im TournamentProvider).
 * Der Abgleich arbeitet auf einem Schnappschuss von Outbox und Stand; was in
 * der halben Sekunde dazwischen eingegeben wurde, fiel vorher aus der Outbox —
 * die Eingabe war da, kam aber nie beim Server an.
 */
export function nachziehen(cfg: SyncConfig, dazwischen: readonly Action[]): SyncConfig {
  if (dazwischen.length === 0) return cfg;
  const bekannt = new Set([...cfg.outbox.map((a) => a.id), ...cfg.remote.map((e) => e.action.id)].filter((id): id is string => !!id));
  const fehlend = dazwischen.filter((a) => a.id && !bekannt.has(a.id));
  return fehlend.length ? { ...cfg, outbox: [...cfg.outbox, ...fehlend] } : cfg;
}

/**
 * Outbox an das Protokoll des Stands angleichen:
 * - Einträge, die im Protokoll nicht (mehr) stehen, fallen weg — sie wurden
 *   zurückgenommen (auch in einem anderen Fenster).
 * - Aktionen im Protokoll, die weder der Server noch die Outbox kennt, kommen
 *   hinten dazu — in Protokoll-Reihenfolge. So gehen Eingaben nicht verloren,
 *   die gemacht wurden, während das Sync-Profil gerade entstand (automatische
 *   Cloud-Sicherung, „Sync einschalten") oder die ein anderes Fenster ohne
 *   eigenen Abgleich eingetragen hat. Doppelt Geschicktes verwirft der Server
 *   über die Kennung.
 *
 * Gibt dasselbe Objekt zurück, wenn sich nichts ändert.
 */
export function outboxAusLog(cfg: SyncConfig, log: readonly Action[]): SyncConfig {
  const imLog = new Set(log.map((a) => a.id).filter((id): id is string => !!id));
  // Ältere Stände: Im Protokoll steht die Aktion ohne Kennung, in der Outbox mit — am Inhalt erkennen
  const ohneId = log.some((a) => !a.id) ? new Set(log.filter((a) => !a.id).map(actionFingerprint)) : null;
  const outbox = cfg.outbox.filter((a) => !a.id || imLog.has(a.id) || !!ohneId?.has(actionFingerprint(a)));
  const bekannt = new Set([...cfg.remote.map((e) => e.action.id), ...outbox.map((a) => a.id)].filter((id): id is string => !!id));
  const fehlend = log.filter((a) => a.id && a.type !== 'undo' && !bekannt.has(a.id));
  if (fehlend.length === 0 && outbox.length === cfg.outbox.length) return cfg;
  return { ...cfg, outbox: [...outbox, ...fehlend] };
}

/** Kennung einer Kollision: die Aktions-ID, sonst der Inhalt */
function rejectKey(action: Action): string {
  return action.id ?? actionFingerprint(action);
}

/**
 * Dieselbe Eingabe kann mehrfach herausfallen — die Zuweisung versucht es
 * offline immer wieder, jedes Mal mit einer neuen Aktion. In der Liste soll sie
 * trotzdem nur einmal stehen; verglichen wird der Inhalt, nicht die Kennung.
 */
function dedupe(list: SyncConfig['rejected']): SyncConfig['rejected'] {
  const seen = new Set<string>();
  const out: SyncConfig['rejected'] = [];
  for (const r of list) {
    // Auch die Uhrzeit bleibt außen vor: derselbe Startversuch eine Minute
    // später ist für die Liste dieselbe Eingabe.
    const key = r.action ? JSON.stringify({ ...r.action, id: undefined, actor: undefined, at: undefined }) : `${r.type}|${r.error}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

/** Nach einer lokalen Aktion: in die Outbox */
export async function enqueue(cfg: SyncConfig, action: Action): Promise<SyncConfig> {
  const next = { ...cfg, outbox: [...cfg.outbox, action] };
  await saveSync(next);
  return next;
}

/** Undo bei aktivem Sync: nur erlaubt, solange die letzte Aktion noch nicht beim Server ist */
export function canUndoSynced(cfg: SyncConfig | null, lastAction: Action | undefined): boolean {
  if (!cfg) return true;
  if (!lastAction?.id) return false;
  return cfg.outbox.some((a) => a.id === lastAction.id);
}

export async function dropFromOutbox(cfg: SyncConfig, id: string): Promise<SyncConfig> {
  const next = { ...cfg, outbox: cfg.outbox.filter((a) => a.id !== id) };
  await saveSync(next);
  return next;
}
